import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { FirestoreService } from '../firestore.service';
import { CareCallGuideDoc, CareCallCanaryDoc } from 'src/app/models/care-call-guide.model';
import {
  ACTIVATION_GUIDE_CANARY_DOC,
  CHF_CARE_CALL_CANARY_DOC,
  isActivationCanaryUser,
  isCareCallCanaryUser,
} from 'src/app/care-call-guide/models/care-call-guide-canary.util';

@Injectable({
  providedIn: 'root',
})
export class CareCallGuideService {
  constructor(private fs: FirestoreService) {}

  getGuide(diagnosis: string): Observable<CareCallGuideDoc> {
    const key = `${diagnosis}_ma`;
    return this.fs.doc$<CareCallGuideDoc>(`welby_configuration/care_call_guides/guides/${key}`);
  }

  /**
   * Whether THIS user is one of the MAs BRD-CLIN-2026-006 was rolled out to.
   *
   * The clinic toggle is not enough on its own — see `isCareCallCanaryUser` for the narrowing and
   * why it fails closed. This is the read half; the rule itself is pure and tested separately.
   *
   * `take(1)` matters as much as it does in `ClientsService.isCareCallAlertsEnabled$`, and for the
   * same two reasons: the caller composes this into a `forkJoin`, which never emits against an
   * Observable that does not complete; and a later write to the canary document must not reassign
   * the gate underneath a form that was already built with the other side of it.
   */
  isCareCallCanaryUser$(userId: string | null | undefined): Observable<boolean> {
    if (!userId) {
      return of(false);
    }
    return this.fs.doc$<CareCallCanaryDoc>(CHF_CARE_CALL_CANARY_DOC).pipe(
      take(1),
      map((doc) => isCareCallCanaryUser(doc, userId)),
      catchError(() => of(false)),
    );
  }

  /**
   * Whether THIS user gets offered the Care Management Activation guide.
   *
   * A mirror of `isCareCallCanaryUser$` on the sibling document, and deliberately a second method
   * rather than a parameter on the first: the two canaries gate different things for different
   * teams, and a shared reader would make "who can see Activation" reachable from a change to the
   * CHF rollout.
   *
   * `take(1)` is not decorative. `CareCallGuideHomeComponent` composes this into a
   * `combineLatest(...).pipe(take(1))`, and an Observable that never completes would leave its
   * `loading` spinner up forever. It also means the gate is read once per guide load — adding a
   * member to the canary shows up on the next reload, not live, which is what the CHF sibling does.
   *
   * Takes an ADDRESS where the CHF sibling takes a uid; `isActivationCanaryUser` documents why the
   * two canaries key their members differently.
   *
   * Fails closed on every path, including a DENIED read: see `ACTIVATION_GUIDE_CANARY_DOC` for why
   * that is not hypothetical today.
   */
  isActivationCanaryUser$(email: string | null | undefined): Observable<boolean> {
    if (!email) {
      return of(false);
    }
    return this.fs.doc$<CareCallCanaryDoc>(ACTIVATION_GUIDE_CANARY_DOC).pipe(
      take(1),
      map((doc) => isActivationCanaryUser(doc, email)),
      catchError(() => of(false)),
    );
  }
}
